import React, { useState } from "react";
import PropTypes from "prop-types";
import { injectIntl } from "react-intl";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Typography from "@material-ui/core/Typography";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import RadioButtonUncheckedIcon from "@material-ui/icons/RadioButtonUnchecked";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import FormTextField from "components/form/form-textfield";

export const PASSWORD_MIN_LENGTH = 12;

export const passwordRequirements = [
  { id: "minLength", test: password => Array.from(password).length >= PASSWORD_MIN_LENGTH },
  { id: "uppercase", test: password => /\p{Lu}/u.test(password) },
  { id: "lowercase", test: password => /\p{Ll}/u.test(password) },
  { id: "digit", test: password => /\p{Nd}/u.test(password) },
  { id: "special", test: password => /[^\p{L}\p{N}\s]/u.test(password) },
  { id: "noSpaces", test: password => password.length > 0 && !/\s/u.test(password) },
];

const requirementStyles = {
  wrapper: { position: "relative" },
  popover: {
    position: "absolute",
    top: "100%",
    left: 0,
    right: 0,
    zIndex: 1300,
    padding: "12px 16px",
    background: "#fff",
    borderRadius: 4,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.25)",
  },
  list: { listStyle: "none", margin: "8px 0 0", padding: 0 },
  item: { display: "flex", alignItems: "center", marginBottom: 4 },
  icon: { fontSize: 20, marginRight: 8 },
  met: { color: "#2e7d32" },
  pending: { color: "#9e9e9e" },
};

const FormPasswordField = ({ intl, value, showRequirements = false, ...props }) => {
  const [visible, setVisible] = useState(false);
  const [focused, setFocused] = useState(false);
  const password = value || "";

  return (
    <div style={requirementStyles.wrapper}>
      <FormTextField
        {...props}
        value={value}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        type={visible ? "text" : "password"}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                aria-label={intl.formatMessage({ id: visible ? "user.password.hide" : "user.password.show" })}
                onClick={() => setVisible(current => !current)}
                onMouseDown={event => event.preventDefault()}
              >
                {visible ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      {showRequirements && focused && (
        <div style={requirementStyles.popover} role="status">
          <Typography variant="body2">{intl.formatMessage({ id: "user.password.requirements.title" })}</Typography>
          <ul style={requirementStyles.list}>
            {passwordRequirements.map(({ id, test }) => {
              const met = test(password);
              const Icon = met ? CheckCircleIcon : RadioButtonUncheckedIcon;
              return (
                <li key={id} style={requirementStyles.item}>
                  <Icon style={{ ...requirementStyles.icon, ...(met ? requirementStyles.met : requirementStyles.pending) }} />
                  <Typography variant="body2">
                    {intl.formatMessage({ id: `user.password.requirements.${id}` }, { min: PASSWORD_MIN_LENGTH })}
                  </Typography>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
};

FormPasswordField.propTypes = {
  /**
   * Valor actual de la contraseña
   */
  value: PropTypes.string,
  /**
   * Muestra el desplegable con los requisitos de la contraseña
   */
  showRequirements: PropTypes.bool,
};

export default injectIntl(FormPasswordField);
